import React, { useState, useRef, useEffect } from 'react';
import { useToastStore } from '../stores/useToastStore';
import { markdownToHtml } from '../utils/markdownUtils';
import { klineDataService } from '../services/klineDataService';
import { estimateTokenCount } from './MyMonitors/utils';
import { LogSource } from '@/services/enums';

interface ContentDialogProps {
  visible?: boolean;
  title: string;
  content: string;
  onClose: () => void;
  useMarkdown?: boolean; // 是否使用 Markdown 渲染
  tokenCount?: number; // token总数（可选）
  loading?: boolean;
  systemPrompt?: string; // 系统提示词（可选，如果提供则显示标签页）
  userPrompt?: string; // 用户提示词（可选，如果提供则显示标签页）
  monitorInfo?: { monitorId: string; symbol: string; bar: string; promptHash?: string } | null; // 监控任务信息（仅提示词对话框使用）
}

const ContentDialog: React.FC<ContentDialogProps> = ({
  visible = true,
  title,
  content,
  onClose,
  useMarkdown = false,
  tokenCount,
  loading = false,
  systemPrompt,
  userPrompt,
  monitorInfo,
}) => {
  const showToast = useToastStore((state) => state.show);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<LogSource>(systemPrompt && userPrompt ? 'system' : 'combined');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(content);
  const [saving, setSaving] = useState(false);
  const [dialogHeight, setDialogHeight] = useState<number | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // 在内容加载完成后记录对话框高度
  useEffect(() => {
    if (content && content !== '加载中...' && !isEditing && dialogRef.current && !dialogHeight) {
      // 使用 setTimeout 确保 DOM 已经渲染完成
      setTimeout(() => {
        if (dialogRef.current) {
          const height = dialogRef.current.offsetHeight;
          setDialogHeight(height);
        }
      }, 100);
    }
  }, [content, isEditing, dialogHeight]);

  if (!visible) return null;

  const handleCopy = async () => {
    try {
      let textToCopy = content;
      if (systemPrompt && userPrompt) {
        if (activeTab === 'system') {
          textToCopy = systemPrompt;
        } else if (activeTab === 'user') {
          textToCopy = userPrompt;
        } else {
          textToCopy = `${systemPrompt}\n\n----------\n\n${userPrompt}`;
        }
      }
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      showToast('已复制到剪贴板', 'success');
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('复制失败:', err);
      showToast('复制失败', 'error');
    }
  };

  // 计算当前显示内容的 token 数量
  const getCurrentTokenCount = (): number => {
    if (!tokenCount) return 0;
    if (systemPrompt && userPrompt) {
      if (activeTab === 'system') {
        return estimateTokenCount(systemPrompt);
      } else if (activeTab === 'user') {
        return estimateTokenCount(userPrompt);
      }
    }
    return tokenCount;
  };

  // 估算token数量
  // 移除本地定义，改用导入的 estimateTokenCount
  // const estimateTokenCount = (text: string): number => { ... }

  // 获取当前显示的内容
  const getCurrentContent = (): string => {
    if (isEditing) {
      return editContent;
    }
    if (systemPrompt && userPrompt) {
      if (activeTab === 'system') {
        return systemPrompt;
      } else if (activeTab === 'user') {
        return userPrompt;
      } else if (activeTab === 'combined') {
        return `${systemPrompt}\n\n----------\n\n${userPrompt}`;
      }
    }
    return content;
  };

  // 解析时间范围（从用户提示词中提取）
  const parseTimeRange = (userPromptText: string): { startTime?: number; endTime?: number } => {
    try {
      // 匹配格式：- **范围**：2025-12-12 08:00:00 至 2025-12-29 20:00:00
      const rangeMatch = userPromptText.match(
        /- \*\*范围\*\*[：:]\s*(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})\s*至\s*(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})/,
      );
      if (rangeMatch) {
        const startDateStr = rangeMatch[1];
        const endDateStr = rangeMatch[2];
        const startTime = Math.floor(new Date(startDateStr).getTime() / 1000);
        const endTime = Math.floor(new Date(endDateStr).getTime() / 1000);
        return { startTime, endTime };
      }
    } catch (error) {
      console.error('解析时间范围失败:', error);
    }
    return {};
  };

  // 拆分提示词（根据----------分隔符）
  const splitPrompt = (fullPrompt: string): { systemPrompt: string; userPrompt: string } => {
    const parts = fullPrompt.split(/\n\n----------\n\n/);
    if (parts.length >= 2) {
      return {
        systemPrompt: parts[0].trim(),
        userPrompt: parts.slice(1).join('\n\n----------\n\n').trim(),
      };
    }
    return {
      systemPrompt: '',
      userPrompt: fullPrompt.trim(),
    };
  };

  // 保存提示词
  const handleSave = async () => {
    if (!monitorInfo) {
      showToast('缺少监控任务信息', 'error');
      return;
    }

    try {
      setSaving(true);

      // 拆分提示词
      const { systemPrompt: newSystemPrompt, userPrompt: newUserPrompt } = splitPrompt(editContent);

      // 解析时间范围
      const { startTime, endTime } = parseTimeRange(newUserPrompt);

      // 生成新的 promptHash（基于完整提示词）
      // 使用 userPrompt 计算 hash（与后端逻辑一致）
      const fullPrompt = newSystemPrompt ? `${newSystemPrompt}\n\n----------\n\n${newUserPrompt}` : newUserPrompt;
      // 使用 Web Crypto API 计算 hash
      const encoder = new TextEncoder();
      const data = encoder.encode(newUserPrompt);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const promptHash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

      // 保存提示词缓存
      await klineDataService.cachePrompt(promptHash, fullPrompt);

      // 更新监控任务的提示词hash和时间范围
      await klineDataService.updateMonitorPrompt(monitorInfo.monitorId, {
        promptHash,
        startTime,
        endTime,
      });

      showToast('保存成功', 'success');
      setIsEditing(false);
      onClose(); // 关闭对话框，让用户刷新页面查看更新
    } catch (error: any) {
      console.error('保存提示词失败:', error);
      showToast(error?.message || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 进入编辑模式
  const handleEdit = () => {
    setEditContent(content);
    setIsEditing(true);
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(content);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="bg-gray-900 border border-white/20 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        style={isEditing && dialogHeight ? { height: `${dialogHeight}px` } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 - 固定在顶部，不会被内容覆盖 */}
        <div className="flex flex-col flex-shrink-0 border-b border-white/10">
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <h3 className="text-lg font-bold text-white">{title}</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCopy}
                className="text-white/60 hover:text-white transition-colors flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10"
                title="复制内容"
              >
                {copied ? (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-xs">已复制</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-xs">复制</span>
                  </>
                )}
              </button>
              {monitorInfo && title === '提示词' && (
                <button
                  onClick={isEditing ? handleCancelEdit : handleEdit}
                  disabled={saving}
                  className="text-white/60 hover:text-white transition-colors flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg border border-blue-500/40 disabled:opacity-50"
                  title={isEditing ? '取消编辑' : '编辑提示词'}
                >
                  {isEditing ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-xs">取消</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span className="text-xs">编辑</span>
                    </>
                  )}
                </button>
              )}
              <button onClick={onClose} className="text-white/60 hover:text-white transition-colors flex-shrink-0">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {/* 标签页（仅在同时有 systemPrompt 和 userPrompt 时显示） */}
          {systemPrompt && userPrompt && (
            <div className="flex border-b border-white/10 px-6">
              <button
                onClick={() => setActiveTab('system')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'system' ? 'text-cyan-400' : 'text-white/60 hover:text-white/80'
                }`}
              >
                系统
                {activeTab === 'system' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.5)]" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('user')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'user' ? 'text-cyan-400' : 'text-white/60 hover:text-white/80'
                }`}
              >
                用户
                {activeTab === 'user' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.5)]" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('combined')}
                className={`px-6 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === 'combined' ? 'text-cyan-400' : 'text-white/60 hover:text-white/80'
                }`}
              >
                完整
                {activeTab === 'combined' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.5)]" />
                )}
              </button>
            </div>
          )}
        </div>
        {/* 内容区域 - 可滚动 */}
        <div className={`flex-1 min-h-0 ${isEditing ? 'p-0' : 'overflow-y-auto px-6 py-4'}`}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <div className="w-10 h-10 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
              <p className="text-cyan-500/70 font-medium animate-pulse">正在处理内容...</p>
            </div>
          ) : isEditing ? (
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full h-full px-6 py-4 bg-white/5 border-0 text-white text-sm font-mono focus:outline-none resize-none"
              placeholder="编辑提示词内容..."
            />
          ) : useMarkdown ? (
            <div
              className="text-sm text-white/80 markdown-content"
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(getCurrentContent()),
              }}
            />
          ) : (
            <pre className="text-sm text-white/80 whitespace-pre-wrap font-mono bg-gray-800/50 p-4 rounded border border-white/10">
              {getCurrentContent()}
            </pre>
          )}
        </div>
        {/* 编辑模式下的保存按钮 */}
        {isEditing && monitorInfo && (
          <div className="flex-shrink-0 px-6 py-3 border-t border-white/10 bg-gray-900/50 flex items-center justify-end gap-3">
            <button
              onClick={handleCancelEdit}
              disabled={saving}
              className="px-4 py-2 bg-white/10 text-white/80 border border-white/20 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 rounded-lg hover:bg-cyan-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>保存中...</span>
                </>
              ) : (
                <span>保存</span>
              )}
            </button>
          </div>
        )}
        {/* 底部显示token总数（仅当提供tokenCount时显示） */}
        {tokenCount !== undefined && (
          <div className="flex-shrink-0 px-6 py-3 border-t border-white/10 bg-gray-900/50">
            <div className="text-xs text-white/60">
              共约 {getCurrentTokenCount().toLocaleString()} 个 token
              {systemPrompt && userPrompt && activeTab !== 'combined' && (
                <span className="ml-2 text-white/40">({activeTab === 'system' ? '系统' : '用户'})</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentDialog;
